/**
 * "Work orders waiting" — Closeout toolbar chip + 4-lane board
 * (WO rework slice 2, Corbin's pick 6 = A, 2026-10-28; mockup C).
 *
 * One list of every work order that isn't signed, checked or sent, sorted by
 * who has the ball. Built on the server from the same rule as the slip audit
 * script, so the office sees exactly what that script prints. Nothing in the
 * bell: the chip IS the alert.
 */
import { useState } from "react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { Dialog } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { confirmSite, siteCheckReviewHref, type SiteCheck } from "@/lib/site-check";
import { cn } from "@/lib/utils";

export type WaitingLane = "tech" | "store" | "office" | "send";

export interface WaitingCard {
    jobId: string;
    displayNumber: string | null;
    customer: string | null;
    store: string | null;
    techName: string | null;
    woNumber: string | null;
    ageDays: number | null;
    group: string;
    lane: WaitingLane;
    why: string;
    next: string;
    action: "open_job" | "open_closeout";
    actionLabel: string;
    /** "Needs a return form": open the customer's Work order forms to pick the default. */
    pickForm?: { customerId: string; label: string } | null;
}

export interface WaitingBoard {
    since: string;
    total: number;
    lanes: Record<WaitingLane, WaitingCard[]>;
    /** "Check the site" (2026-10-29): held from dispatch until confirmed. Counted in total. */
    siteChecks?: SiteCheck[];
}

export const WO_WAITING_KEY = ["close-out", "work-orders-waiting"] as const;

/** Lane order, words and colors. Color carries the meaning; words back it up. */
export const LANES: Array<{ lane: WaitingLane; who: string; sub: string; color: string }> = [
    { lane: "tech", who: "Tech", sub: "never filled the WO", color: "#1d7a46" },
    { lane: "store", who: "Store", sub: "no signature or stamp", color: "#c0312b" },
    { lane: "office", who: "Office", sub: "check it or find it", color: "#004AAD" },
    { lane: "send", who: "Send", sub: "ready, or went out wrong", color: "#b26a00" },
];

/** A card this many days old reads red. */
export const OLD_DAYS = 7;

export function cardMeta(c: WaitingCard): string {
    return [c.customer, c.techName, c.woNumber ? `WO ${c.woNumber}` : "no WO number"].filter(Boolean).join(" · ");
}

export function ageText(days: number | null): string {
    if (days == null) return "";
    return days === 0 ? "today" : `${days} d`;
}

function useWaitingBoard() {
    return useQuery({
        queryKey: WO_WAITING_KEY,
        queryFn: () => api.get<WaitingBoard>("/api/close-out/work-orders-waiting"),
        staleTime: 60_000,
        refetchInterval: 5 * 60_000,
    });
}

/** The chip on the Closeout toolbar. Gray at 0, amber with a count. */
export function WoWaitingChip({ dark }: { dark?: boolean }) {
    const q = useWaitingBoard();
    const [open, setOpen] = useState(false);
    const n = q.data?.total ?? 0;
    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="wo-waiting-chip"
                title="Every work order that isn't signed, checked or sent, sorted by who has it"
                className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-bold",
                    n > 0
                        ? "border-amber-500 bg-amber-500/15 text-amber-700 dark:text-amber-300"
                        : dark
                          ? "border-white/25 text-shell-foreground/80"
                          : "border-border text-foreground/70",
                )}
            >
                <span className="h-2 w-2 rounded-full" style={{ background: n > 0 ? "#b26a00" : "#7b8594" }} />
                Work orders waiting{q.data ? ` · ${n}` : ""}
            </button>
            {open && (
                <WoWaitingBoardDialog board={q.data ?? null} loading={q.isLoading} onClose={() => setOpen(false)} />
            )}
        </>
    );
}

export function WoWaitingBoardDialog({
    board,
    loading,
    onClose,
}: {
    board: WaitingBoard | null;
    loading: boolean;
    onClose: () => void;
}) {
    const navigate = useNavigate();
    const go = (c: WaitingCard) => {
        onClose();
        navigate(c.action === "open_closeout" ? `/close-out?job=${c.jobId}` : `/jobs/${c.jobId}`);
    };
    return (
        <Dialog open onClose={onClose} title="Work orders waiting" className="max-w-6xl w-[96vw]">
            <p className="mb-3 text-sm text-foreground/70">
                Every work order that isn&apos;t signed, checked or sent, by who has the ball. A card leaves when its
                packet goes out with the signed work order.
            </p>
            {loading && <p className="text-sm">Loading…</p>}
            {board && board.total === 0 && (
                <p className="rounded-lg border p-4 text-base font-semibold" data-testid="wo-waiting-empty">
                    Nothing waiting. Every work order is signed, checked and sent.
                </p>
            )}
            {board && (board.siteChecks?.length ?? 0) > 0 && (
                <SiteChecks
                    checks={board.siteChecks ?? []}
                    onOpen={(path) => {
                        onClose();
                        navigate(path);
                    }}
                />
            )}
            {board && board.total - (board.siteChecks?.length ?? 0) > 0 && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="wo-waiting-board">
                    {LANES.map(({ lane, who, sub, color }) => {
                        const cards = board.lanes[lane] ?? [];
                        return (
                            <section
                                key={lane}
                                aria-label={`${who}: ${cards.length}`}
                                className="flex min-w-0 flex-col gap-2 rounded-xl border bg-card p-3"
                                data-testid={`wo-lane-${lane}`}
                            >
                                <div className="h-1.5 rounded" style={{ background: color }} />
                                <header className="flex items-center gap-2.5">
                                    <span className="text-3xl font-extrabold leading-none tabular-nums">
                                        {cards.length}
                                    </span>
                                    <span className="text-base font-bold leading-tight">
                                        {who}
                                        <small className="block text-xs font-normal text-foreground/60">{sub}</small>
                                    </span>
                                </header>
                                {cards.length === 0 && <p className="text-sm text-foreground/50">Nothing here.</p>}
                                {cards.map((c) => (
                                    <article
                                        key={c.jobId}
                                        className="flex flex-col gap-1.5 rounded-lg border bg-background p-2.5"
                                    >
                                        <div className="flex items-baseline justify-between gap-2">
                                            <span className="font-bold leading-tight">
                                                {c.store ?? c.customer ?? `Job #${c.displayNumber ?? ""}`}
                                            </span>
                                            <span
                                                className={cn(
                                                    "whitespace-nowrap font-mono text-xs",
                                                    (c.ageDays ?? 0) >= OLD_DAYS && "font-semibold text-red-600",
                                                )}
                                            >
                                                {ageText(c.ageDays)}
                                            </span>
                                        </div>
                                        <span className="text-xs text-foreground/60">{cardMeta(c)}</span>
                                        <span className="text-sm">{c.why}</span>
                                        {lane === "store" && <SendForSignature jobId={c.jobId} color={color} />}
                                        {c.pickForm && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    onClose();
                                                    navigate(`/customers/${c.pickForm!.customerId}?tab=work-orders`);
                                                }}
                                                className="rounded-md border-2 bg-transparent px-2.5 py-2 text-sm font-bold"
                                                style={{ borderColor: color, color }}
                                            >
                                                {c.pickForm.label}
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => go(c)}
                                            title={c.next}
                                            className={cn(
                                                "rounded-md px-2.5 py-2 text-sm font-bold",
                                                lane === "store" ? "border-2 bg-transparent" : "text-white",
                                            )}
                                            style={
                                                lane === "store" ? { borderColor: color, color } : { background: color }
                                            }
                                        >
                                            {lane === "store" ? "Open job" : c.actionLabel}
                                        </button>
                                    </article>
                                ))}
                            </section>
                        );
                    })}
                </div>
            )}
        </Dialog>
    );
}

/**
 * WO rework slice 7: "Send for signature" — email the store manager a link to
 * sign the completed work order on their phone.
 */
export function SendForSignature({ jobId, color }: { jobId: string; color: string }) {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [sentTo, setSentTo] = useState<string | null>(null);
    if (sentTo) {
        return <span className="text-sm font-semibold text-green-700">Link sent to {sentTo}</span>;
    }
    if (!open) {
        return (
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="rounded-md px-2.5 py-2 text-sm font-bold text-white"
                style={{ background: color }}
            >
                Send for signature
            </button>
        );
    }
    return (
        <form
            className="flex flex-col gap-1.5"
            onSubmit={(e) => {
                e.preventDefault();
                setBusy(true);
                api.post<{ sentTo: string }>(`/api/jobs/${jobId}/signature-request`, { email })
                    .then((r) => setSentTo(r.sentTo))
                    .catch((err: { body?: { message?: string } }) =>
                        toast.error(err?.body?.message ?? "The link didn't send. Try again."),
                    )
                    .finally(() => setBusy(false));
            }}
        >
            <label className="text-xs text-foreground/70">
                Store manager&apos;s email
                <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-0.5 w-full rounded-md border-2 px-2 py-1.5 text-sm text-foreground"
                    placeholder="manager@store.com"
                />
            </label>
            <button
                type="submit"
                disabled={busy}
                className="rounded-md px-2.5 py-2 text-sm font-bold text-white disabled:opacity-60"
                style={{ background: color }}
            >
                {busy ? "Sending…" : "Send the link"}
            </button>
        </form>
    );
}

/**
 * "Check the site" (trust fix, 2026-10-29): work orders intake wasn't sure of
 * the store for. Calm amber, above the lanes. These jobs can't be dispatched
 * until someone in the office looks: the paper's address next to the job's.
 */
export function SiteChecks({ checks, onOpen }: { checks: SiteCheck[]; onOpen: (path: string) => void }) {
    const qc = useQueryClient();
    const { can } = useAuth();
    const canConfirm = can("jobs.manage");
    const [busy, setBusy] = useState<string | null>(null);
    const confirm = (jobId: string) => {
        setBusy(jobId);
        confirmSite(jobId)
            .then(() => {
                toast.success("Site confirmed. The job can go to a tech.");
                void qc.invalidateQueries({ queryKey: WO_WAITING_KEY });
                void qc.invalidateQueries({ queryKey: ["job", jobId] });
            })
            .catch(() => toast.error("Couldn't save that. Try again."))
            .finally(() => setBusy(null));
    };
    return (
        <section
            className="mb-3 flex flex-col gap-2 rounded-xl border border-amber-400 bg-amber-50 p-3 text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
            data-testid="wo-site-checks"
            aria-label={`Check the site: ${checks.length}`}
        >
            <header className="flex items-center gap-2.5">
                <span className="text-3xl font-extrabold leading-none tabular-nums">{checks.length}</span>
                <span className="text-base font-bold leading-tight">
                    Check the site
                    <small className="block text-xs font-normal opacity-80">
                        We weren&apos;t sure which store. These can&apos;t go to a tech until someone checks.
                    </small>
                </span>
            </header>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {checks.map((c) => (
                    <article
                        key={c.jobId}
                        className="flex flex-col gap-1.5 rounded-lg border border-amber-300 bg-background p-2.5 text-foreground"
                    >
                        <span className="text-xs text-foreground/60">
                            {[
                                c.customer,
                                c.woNumber ? `WO ${c.woNumber}` : null,
                                c.displayNumber ? `#${c.displayNumber}` : null,
                            ]
                                .filter(Boolean)
                                .join(" · ")}
                        </span>
                        <span className="text-sm">
                            <b>Work order says:</b> {c.woSays ?? "no address on the work order"}
                        </span>
                        <span className="text-sm">
                            <b>Job is at:</b> {c.jobSite ?? "no site yet"}
                        </span>
                        <div className="flex flex-wrap gap-2">
                            {canConfirm && (
                                <button
                                    type="button"
                                    disabled={busy === c.jobId}
                                    onClick={() => confirm(c.jobId)}
                                    className="rounded-md bg-amber-600 px-2.5 py-2 text-sm font-bold text-white disabled:opacity-60"
                                    data-testid="wo-site-confirm"
                                >
                                    Site is right
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => onOpen(siteCheckReviewHref(c))}
                                className="rounded-md border-2 border-amber-600 px-2.5 py-2 text-sm font-bold text-amber-800 dark:text-amber-200"
                            >
                                Check the work order
                            </button>
                        </div>
                    </article>
                ))}
            </div>
        </section>
    );
}
